import { useParams } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { FestivalSidebar } from "@/components/FestivalSidebar";
import { FestivalBody, Section } from "@/components/FestivalSection";
import { Carousel } from "@/components/Carousel";
import { VideoCard } from "@/components/VideoCard";
import { resolveUrl } from "@/lib/api";

const Trailer = ({ trailer, name }) => (
  <div className="aspect-video rounded-2xl overflow-hidden bg-black shadow-[0_8px_30px_rgba(0,0,0,0.12)]" data-testid="festival-trailer">
    {trailer?.embed_url ? (
      <iframe
        src={trailer.embed_url}
        title={`${name} trailer`}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center text-white/70 text-[14px] px-6 text-center" data-testid="festival-trailer-empty">
        {["uploading", "processing"].includes(trailer?.status) ? "Trailer is still processing — check back soon." : "Trailer coming soon."}
      </div>
    )}
  </div>
);

const Experience = ({ videos }) => {
  if (!videos?.length) return null;
  return (
    <Section title="Here’s What You’ll Experience" testId="festival-experience">
      <Carousel items={videos} render={(v) => <VideoCard video={v} />} perView={{ base: 1.1, md: 2, "2xl": 3 }} gap={20} testId="festival-experience-carousel" />
    </Section>
  );
};

export default function Festival() {
  const { id } = useParams();
  const { data: festival, error } = useApi(`/festivals/${id}`);

  if (error) {
    return (
      <div className="mt-24 text-center" data-testid="festival-error">
        <p className="text-[22px] font-bold">Festival not found</p>
        <p className="mt-2 text-[15px] text-[#8A8A8A]">It may have been removed or the link is incorrect.</p>
      </div>
    );
  }
  if (!festival) return null;

  return (
    <div className="mt-6 lg:mt-8 grid gap-6 lg:gap-10 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] 2xl:grid-cols-[minmax(0,1fr)_340px]" data-testid="festival-page">
      <div className="min-w-0 lg:col-start-1 lg:row-start-1">
        <h1 className="text-[22px] md:text-[26px] lg:text-[28px] font-bold leading-tight mb-3 md:mb-4" data-testid="festival-page-title">
          {festival.name}
        </h1>
        <Trailer trailer={festival.trailer} name={festival.name} />
      </div>
      <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
        <FestivalSidebar festival={festival} ctaLabel="Join Festival" targetId="passes" ctaTestId="festival-join-button" />
      </div>
      <div className="min-w-0 lg:col-start-1 lg:row-start-2">
        <div className="space-y-10 md:space-y-12">
          {festival.logo_url && (
            <div className="bg-white rounded-2xl py-6 md:py-8 px-6 md:px-10 flex justify-center shadow-[0_2px_12px_rgba(0,0,0,0.03)]" data-testid="festival-logo-banner">
              <img src={resolveUrl(festival.logo_url)} alt={`${festival.name} logo`} className="h-[60px] md:h-[90px] object-contain" />
            </div>
          )}
          <FestivalBody festival={festival} experience={<Experience videos={festival.videos} />} />
        </div>
      </div>
    </div>
  );
}
